import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Rating,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import {
  useGetTestimonialsQuery,
  useDeleteTestimonialMutation,
} from "./api/testimonialApi";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import { Testimonial } from "./types/testimonal";
import AddEditTestimonial from "./componets/AddEditTestimonial";

const TestimonialManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonial | null>(null);
  const navigate = useNavigate();

  const {
    data: testimonials,
    isLoading,
    isError,
    isFetching,
  } = useGetTestimonialsQuery();
  const [deleteTestimonial] = useDeleteTestimonialMutation();

  const columns: Array<MRT_ColumnDef<Testimonial>> = [
    {
      header: "Photo",
      accessorKey: "photo",
      size: 80,
      Cell({ row: { original } }) {
        return (
          <img
            src={`${original.photo}`}
            alt="Photo"
            width={60}
            style={{ borderRadius: 6 }}
          />
        );
      },
    },
    {
      header: "Name",
      accessorKey: "name",
      size: 150,
    },
    {
      header: "Position",
      accessorKey: "position",
      size: 150,
    },
    {
      header: "Message",
      accessorKey: "message",
      size: 300,
    },
 
    {
      header: "Rating",
      accessorKey: "rating",
      size: 100,
      Cell({ cell }) {
        return <Rating value={parseFloat(cell.getValue() as string)} readOnly />;
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 120,
      Cell({ cell }) {
        const status = cell.getValue() as string;
        return (
          <Chip
            label={status.toUpperCase()}
            color={status === "approved" ? "success" : "default"}
            variant="outlined"
          />
        );
      },
    },
  ];

  return (
    <Box>
      <DataTable<Testimonial>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Testimonials</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${testimonials?.data?.length ?? 0} Testimonials`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedTestimonial(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Testimonial
            </Button>
          </Box>
        }
        list={testimonials?.data ?? []}
        count={testimonials?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTestimonial(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTestimonial(row);
                setOpenDialog(true);
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        // onRowClick={(row) => navigate(`/testimonial/${row.id}`)}
      />

      <AddEditTestimonial
        open={openDialog}
        testimonial={selectedTestimonial}
        setModalOpen={setOpenDialog}
      />

      {selectedTestimonial && (
        <ConfirmModal
          type="primary"
          title="Delete Testimonial"
          message={`Are you sure you want to delete testimonial from '${selectedTestimonial.name}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteTestimonial({id: selectedTestimonial.id.toString(), body:null});
            setOpenConfirmModal(false);
            setSelectedTestimonial(null);
          }}
        />
      )}
    </Box>
  );
};

export default TestimonialManager;
